// src/marketing/docs/DocsPage.tsx
import { useState } from 'react'
import { useAppStore } from '../../shell/appStore'
import { ApiClientError } from '../../lib/api'
import {
  useMarketingDocs,
  useCreateMarketingDoc,
  useUpdateMarketingDoc,
  useDeleteMarketingDoc,
} from './hooks/useMarketingDocs'
import { renderMarkdown } from './markdown'
import { DocHeader } from '../editorial/DocHeader'
import { Prose } from '../editorial/Prose'
import { ReadingProgress } from '../editorial/ReadingProgress'
import type { MarketingDoc } from '../../lib/types'

// ── Styles ──────────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  padding: '1.5rem 2rem',
  height: '100%',
  boxSizing: 'border-box',
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
}

const layoutStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1rem',
  flex: 1,
  minHeight: 0,
}

const sidebarStyle: React.CSSProperties = {
  width: '220px',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  overflowY: 'auto',
}

const paneStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  minWidth: 0,
  overflowY: 'auto',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  background: 'var(--elevated)',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
}

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  height: '320px',
  resize: 'vertical',
  fontFamily: 'monospace',
  fontSize: '0.8125rem',
}

const previewStyle: React.CSSProperties = {
  height: '320px',
  overflowY: 'auto',
  padding: '0.75rem 1rem',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  background: 'var(--bg)',
  fontSize: '0.875rem',
  color: 'var(--fg)',
  lineHeight: '1.6',
}

const btnPrimaryStyle: React.CSSProperties = {
  padding: '0.4375rem 1rem',
  background: 'var(--arancio)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const btnGhostStyle: React.CSSProperties = {
  padding: '0.4375rem 0.75rem',
  background: 'transparent',
  color: 'var(--muted)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  fontSize: '0.875rem',
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const btnDangerStyle: React.CSSProperties = {
  padding: '0 6px',
  background: 'transparent',
  color: 'var(--muted)',
  border: 'none',
  borderRadius: '4px',
  fontSize: '0.75rem',
  cursor: 'pointer',
  fontFamily: 'inherit',
  flexShrink: 0,
}

const toastStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: '1.5rem',
  right: '1.5rem',
  padding: '0.625rem 1rem',
  background: 'var(--elevated)',
  border: '1px solid var(--line)',
  borderRadius: '8px',
  fontSize: '0.875rem',
  color: 'var(--fg)',
  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  zIndex: 100,
}

// ── Composant ────────────────────────────────────────────────────────────────

export function DocsPage() {
  const { activeAppId } = useAppStore()

  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editBody, setEditBody] = useState('')
  const [toast, setToast] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data: docs = [], isLoading } = useMarketingDocs(activeAppId)
  const createMut = useCreateMarketingDoc()
  const updateMut = useUpdateMarketingDoc()
  const deleteMut = useDeleteMarketingDoc()

  const selectedDoc = docs.find((d) => d.id === selectedDocId) ?? null

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2500)
  }

  function selectDoc(doc: MarketingDoc) {
    setSelectedDocId(doc.id)
    setEditTitle(doc.title)
    setEditBody(doc.bodyMd)
    setEditing(false)
  }

  async function handleCreate() {
    if (!activeAppId) return
    setError(null)
    const slug = `doc-${Date.now()}`
    try {
      const doc = await createMut.mutateAsync({
        appId: activeAppId,
        slug,
        title: 'Nouveau document',
        sortOrder: docs.length,
      })
      setSelectedDocId(doc.id)
      setEditTitle(doc.title)
      setEditBody(doc.bodyMd)
      setEditing(true)
      showToast('Document créé')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Erreur inattendue')
    }
  }

  async function handleSave() {
    if (!selectedDocId) return
    setError(null)
    try {
      await updateMut.mutateAsync({ id: selectedDocId, data: { title: editTitle, bodyMd: editBody } })
      setEditing(false)
      showToast('Document sauvegardé')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Erreur inattendue')
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Supprimer ce document ?')) return
    setError(null)
    try {
      await deleteMut.mutateAsync(id)
      if (selectedDocId === id) {
        setSelectedDocId(null)
        setEditTitle('')
        setEditBody('')
        setEditing(false)
      }
      showToast('Document supprimé')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Erreur inattendue')
    }
  }

  async function handleMoveUp(doc: MarketingDoc, idx: number) {
    if (idx === 0) return
    const neighbor = docs[idx - 1]
    if (!neighbor) return
    await Promise.all([
      updateMut.mutateAsync({ id: doc.id, data: { sortOrder: neighbor.sortOrder } }),
      updateMut.mutateAsync({ id: neighbor.id, data: { sortOrder: doc.sortOrder } }),
    ])
  }

  async function handleMoveDown(doc: MarketingDoc, idx: number) {
    if (idx === docs.length - 1) return
    const neighbor = docs[idx + 1]
    if (!neighbor) return
    await Promise.all([
      updateMut.mutateAsync({ id: doc.id, data: { sortOrder: neighbor.sortOrder } }),
      updateMut.mutateAsync({ id: neighbor.id, data: { sortOrder: doc.sortOrder } }),
    ])
  }

  const sortedDocs = [...docs].sort((a, b) => a.sortOrder - b.sortOrder)
  const selectedIdx = sortedDocs.findIndex((d) => d.id === selectedDocId)

  return (
    <div data-testid="marketing-docs" style={pageStyle}>
      {/* En-tête */}
      <div className="no-print" style={headerStyle}>
        <h1 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--fg)', margin: 0 }}>
          Dossier stratégique
        </h1>
        <button
          style={btnPrimaryStyle}
          onClick={() => { void handleCreate() }}
          disabled={createMut.isPending || !activeAppId}
          aria-label="Nouveau document"
        >
          + Nouveau document
        </button>
      </div>

      {/* Layout principal */}
      <div style={layoutStyle}>
        {/* Rail gauche */}
        <aside className="no-print" style={sidebarStyle}>
          {isLoading && (
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Chargement…</p>
          )}
          {!isLoading && docs.length === 0 && (
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
              Aucun document. Créez-en un !
            </p>
          )}
          {sortedDocs.map((doc, idx) => {
            const isActive = doc.id === selectedDocId
            const originalIdx = docs.findIndex((d) => d.id === doc.id)
            return (
              <div
                key={doc.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.375rem 0.5rem',
                  borderRadius: '6px',
                  fontSize: '0.8125rem',
                  cursor: 'pointer',
                  transition: 'background 0.1s',
                  background: isActive ? 'var(--elevated)' : 'transparent',
                  border: `1px solid ${isActive ? 'var(--line)' : 'transparent'}`,
                  color: isActive ? 'var(--arancio)' : 'var(--muted)',
                }}
              >
                {/* Numéro */}
                <span
                  style={{
                    fontFamily: 'var(--mono, monospace)',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    flexShrink: 0,
                    color: isActive ? 'var(--arancio)' : 'var(--dim, var(--muted))',
                  }}
                >
                  {String(idx).padStart(2, '0')}
                </span>
                {/* Flèches de réordonnancement */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  <button
                    style={{ ...btnGhostStyle, padding: '0 4px', fontSize: '10px' }}
                    onClick={() => { void handleMoveUp(doc, originalIdx) }}
                    disabled={originalIdx === 0}
                    aria-label="Remonter"
                    title="Remonter"
                  >
                    ↑
                  </button>
                  <button
                    style={{ ...btnGhostStyle, padding: '0 4px', fontSize: '10px' }}
                    onClick={() => { void handleMoveDown(doc, originalIdx) }}
                    disabled={originalIdx === docs.length - 1}
                    aria-label="Descendre"
                    title="Descendre"
                  >
                    ↓
                  </button>
                </div>
                {/* Titre cliquable */}
                <span
                  style={{
                    flex: 1,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => selectDoc(doc)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') selectDoc(doc) }}
                >
                  {doc.title}
                </span>
                {/* Suppression */}
                <button
                  style={btnDangerStyle}
                  onClick={() => { void handleDelete(doc.id) }}
                  aria-label={`Supprimer ${doc.title}`}
                >
                  ✕
                </button>
              </div>
            )
          })}
        </aside>

        {/* Panneau principal */}
        {selectedDoc ? (
          editing ? (
            /* ── Mode édition ── */
            <div style={paneStyle}>
              <div>
                <label
                  htmlFor="doc-title"
                  style={{ fontSize: '0.8rem', color: 'var(--muted)', display: 'block', marginBottom: '0.25rem' }}
                >
                  Titre
                </label>
                <input
                  id="doc-title"
                  style={inputStyle}
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  aria-label="Titre du document"
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <label
                    htmlFor="doc-body"
                    style={{ fontSize: '0.8rem', color: 'var(--muted)', display: 'block', marginBottom: '0.25rem' }}
                  >
                    Contenu (Markdown)
                  </label>
                  <textarea
                    id="doc-body"
                    style={textareaStyle}
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    aria-label="Contenu"
                    placeholder={'# Mon titre\n\nTexte en Markdown…'}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0 0 0.25rem 0' }}>
                    Aperçu
                  </p>
                  <div
                    style={previewStyle}
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(editBody) }}
                    data-testid="preview"
                  />
                </div>
              </div>
              <div className="no-print" style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  style={btnPrimaryStyle}
                  onClick={() => { void handleSave() }}
                  disabled={updateMut.isPending}
                >
                  {updateMut.isPending ? 'Sauvegarde…' : 'Enregistrer'}
                </button>
                <button
                  style={btnGhostStyle}
                  onClick={() => {
                    setSelectedDocId(null)
                    setEditTitle('')
                    setEditBody('')
                    setEditing(false)
                  }}
                >
                  Fermer
                </button>
              </div>
            </div>
          ) : (
            /* ── Mode lecture ── */
            <div style={paneStyle}>
              <ReadingProgress />
              <DocHeader
                no={String(selectedIdx >= 0 ? selectedIdx : 0).padStart(2, '0')}
                title={selectedDoc.title}
                meta={selectedDoc.slug}
              />
              <Prose md={selectedDoc.bodyMd} />
              <div className="no-print" style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  style={btnGhostStyle}
                  data-testid="doc-edit-toggle"
                  onClick={() => setEditing(true)}
                >
                  Éditer
                </button>
              </div>
            </div>
          )
        ) : (
          <div style={{ flex: 1, color: 'var(--muted)', fontSize: '0.875rem', padding: '1.5rem' }}>
            Sélectionnez un document pour le lire.
          </div>
        )}
      </div>

      {error && (
        <div
          style={{ ...toastStyle, borderColor: 'var(--danger, #e55)', color: 'var(--danger, #e55)' }}
          role="alert"
          aria-live="assertive"
          data-testid="docs-error"
        >
          {error}
        </div>
      )}
      {toast && (
        <div style={toastStyle} role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </div>
  )
}
